/**
 * «Pedir de nuevo» un documento (DEF-043 y DEF-066, QA de TEST 05 y 07-oct-2026).
 *
 * POR QUÉ EXISTE
 * --------------
 * El botón solo precargaba un borrador. No cambiaba el requisito —la Ficha
 * seguía en 5/5 sobre un documento que el asesor le estaba pidiendo de nuevo
 * al cliente— y al enviarlo el asesor TOMABA la conversación: el bot se
 * callaba justo cuando el cliente iba a reenviar, y el reenvío llegaba sin
 * que nadie lo acusara.
 *
 * El mensaje sale con `pedido_de_nuevo` —el documento al que se refiere— y
 * la conversación lo manda como del asesor sin quitarle la conducción al
 * agente. El chip del compositor dice a qué documento se refiere, y quitarlo
 * vuelve al envío normal (que sí toma la conversación).
 *
 * EL REQUISITO CAMBIA AL ENVIAR, NO AL ABRIR (DEF-066)
 * ---------------------------------------------------
 * Hasta el 07-oct-2026 el clic llamaba a `POST /documents/{id}/request-again`
 * y el requisito quedaba «pedido de nuevo» en el acto. Nada lo deshacía: el ✕
 * del chip solo sacaba el chip y el borrador quedaba cargado, listo para salir
 * como una respuesta normal que toma la conversación. Hoy el clic no llama a
 * nada —arma el chip con la tarjeta del documento y precarga el borrador— y
 * el cambio lo hace la conversación cuando el mensaje sale
 * (`_handle_manual_reply`). El ✕ se lleva también el borrador si nadie lo
 * tocó (`borradorSinElPedido`).
 *
 * Funciones puras: las corren `tests/unit/test_qa_test_05oct_pedir_de_nuevo.py`
 * y `test_qa_test_07oct_portal.py` en Node, contra los mismos estados que
 * decide el backend.
 */

/**
 * El borrador que precarga «Pedir de nuevo». UNO para los seis motivos de
 * rechazo, y no nombra ninguno: lo fijó negocio (DEF-116, QA de TEST
 * 22-oct-2026). El anterior —«la imagen no se lee bien»— afirmaba la causa
 * «ilegible» también sobre un recibo rechazado por «No es el documento», y el
 * cliente reenviaba una foto más nítida del recibo equivocado.
 */
export const BORRADOR_PEDIR_DE_NUEVO =
  'No hemos podido validar el documento que nos enviaste. ¿Podrías enviarnos '
  + 'uno nuevo? Puede ser una foto más nítida o en PDF.'

export interface PedidoDeNuevo {
  docId: string
  /** «Copia Literal (SUNARP)»: lo que el chip muestra primero. */
  tipo: string
  filename: string
}

/**
 * El pedido a partir de la tarjeta del documento que el asesor tiene delante
 * (DEF-066): `type` es la etiqueta del portal (`friendly_doc_type`), la misma
 * que devolvía `/request-again`, así que el chip dice lo mismo sin pedirle
 * nada al servidor.
 */
export function pedidoDeNuevo(
  docId: string,
  doc?: { type?: string; filename?: string } | null,
): PedidoDeNuevo {
  return {
    docId,
    tipo: (doc?.type || '').trim(),
    filename: (doc?.filename || '').trim(),
  }
}

/**
 * El borrador después de quitar el chip (DEF-066). Si es el precargado tal
 * cual, se va con el chip: sin el chip saldría como una respuesta normal, que
 * TOMA la conversación, y eso nadie lo eligió. Si el asesor lo tocó, es suyo
 * y se queda.
 */
export function borradorSinElPedido(borrador: string): string {
  return borrador === BORRADOR_PEDIR_DE_NUEVO ? '' : borrador
}

/** «Pidiendo de nuevo: Copia Literal (SUNARP) · CL.pdf». */
export function etiquetaDelPedido(p: PedidoDeNuevo): string {
  const partes = [p.tipo || 'Documento', p.filename].filter((x) => x)
  return `Pidiendo de nuevo: ${partes.join(' · ')}`
}

/**
 * El cuerpo de `POST /conversations/reply`. Con el chip puesto viaja el
 * documento; sin él es la respuesta de siempre, y el backend no puede
 * distinguirla de una que nunca tuvo chip.
 */
export function cuerpoDeRespuesta(
  conversationId: string, texto: string, pedido: PedidoDeNuevo | null,
): { conversation_id: string; text: string; pedido_de_nuevo?: string } {
  return pedido
    ? { conversation_id: conversationId, text: texto, pedido_de_nuevo: pedido.docId }
    : { conversation_id: conversationId, text: texto }
}

/**
 * Los estados en que una PERSONA ya atiende. Ahí el pedido sale por el camino
 * de siempre: no hay conducción que devolverle al agente. Es la misma lista
 * que `conversation/_pedir_de_nuevo_sin_tomar`, cruzada por test.
 */
export const ATIENDE_UNA_PERSONA = ['HUMAN_HANDOFF', 'AWAITING_SLA_CHOICE']

/** ¿Al enviar con el chip, el agente sigue conduciendo? */
export function elAgenteSigue(conversationState: string | null | undefined): boolean {
  return !ATIENDE_UNA_PERSONA.includes((conversationState || '').toUpperCase())
}

/**
 * Lo que el asesor lee sobre el compositor con el chip puesto. Reemplaza al
 * «si escribes, tomas la conversación» de `estadoAtencion`, que con el chip
 * deja de ser cierto. `null` si una persona ya atiende: ahí no cambia nada.
 */
export function avisoDelPedido(conversationState: string | null | undefined): string | null {
  return elAgenteSigue(conversationState)
    ? 'Sale como mensaje tuyo y el agente IA sigue conduciendo para recibir el reenvío: '
      + 'no tomas la conversación.'
    : null
}
